import styled from 'styled-components';
import {
  List as DropdownList,
  Item as DropdownItem,
  Title as DropdownTitle
} from '@spa/shared/components/Dropdown/styles';
import { aboveMin } from '@spa/shared/theme';
import { Container as LanguageSelector } from '@spa/shared/components/LanguageSelector/styles';

export const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2;
  background: ${props => props.theme.header.background};

  svg {
    &.logo {
      width: 140px;
    }

    &#arrow-down {
      path {
        stroke: ${props => props.theme.header.dropdown.icon};
      }
    }
  }

  .middle-menu {
    margin-left: ${props => props.theme.space(40)};
    gap: ${props => props.theme.space(5)};
  }

  .authlist {
    li:last-child {
      margin-right: ${props => props.theme.space(5)};

      & > div {
        height: 40px;
        border-radius: 8px;
        padding-inline: ${props => props.theme.space(3)};

        &:hover {
          background: ${props => props.theme.header.hamburgerHover};
        }

        label {
          display: flex;
          align-items: center;
          height: 40px;
          color: ${props => props.theme.header.link.authlistLinkColor};
          gap: ${props => props.theme.space(4)};
        }

        svg {
          g {
            fill: ${props => props.theme.header.hamburger};
          }
        }
      }
    }

    & > li {
      & > a {
        display: flex;
        align-items: center;
        height: 40px;
        border-radius: 8px;
        padding-inline: 12px;
        padding-block: 0;

        &:hover {
          background: ${props => props.theme.header.authLinksBackground};
          border: none;
        }
      }
    }

    li.checkout-signup {
      & > div {
        &:hover {
          background-color: transparent;
        }

        label {
          color: white;
          &:hover {
            background-color: transparent;
          }

          svg#arrow-down {
            margin-top: 0;
            path {
              stroke: white;
            }
          }
        }
      }
    }
  }

  ${aboveMin.extraLargeBreakpoint`
    padding-left: 10%;
    padding-right: 10%;
  `}
`;

export const List = styled.ul`
  display: flex;
  justify-content: ${props => (props.hasTabs ? 'space-between' : 'flex-end')};
  align-items: center;
  gap: ${props => props.theme.space(3)};
`;

export const Item = styled.li`
  position: relative;

  &:hover {
    cursor: pointer;
  }

  a {
    font-family: IntervalNextReg, sans-serif;
    font-size: ${props => props.theme.rem(14)};
    color: ${props => props.theme.header.link.authlistLinkColor};
    text-decoration: none;
    padding-bottom: ${props => props.theme.space(2)};

    &:hover {
      text-decoration: none;
      color: ${props => props.theme.header.link.color};
      border-bottom: 2px solid ${props => props.theme.header.link.color};
    }

    &.signup {
      display: flex;
      align-items: center;
      height: 38px;
      padding: 0 17px;
      background-color: ${props => props.theme.header.link.signup};
      color: ${props => props.theme.header.link.color};
      border-radius: 8px;
      font-size: 14px;

      &:hover {
        padding: 0 17px;
        border-bottom: 0;
      }
    }

    &.become-landlord {
      border: 2px solid ${props => props.theme.header.link.color};
      padding: ${props => props.theme.space(1)} ${props => props.theme.space(3)};
      border-radius: 8px;
      transition: background-color 0.5s ease-out;

      &:hover {
        background-color: ${props => props.theme.header.link.color};
        color: ${props => props.theme.typography.bodyColor};
      }
    }

    &.list-your-place {
      display: flex;
      align-items: center;
      align-self: flex-end;
      height: 31px;
      padding: ${props => props.theme.space(2)} ${props => props.theme.space(3)};
      background: ${props => props.theme.header.list_your_place.button_background_color};
      font-family: IntervalNextReg, sans-serif;
      font-weight: 600;
      font-size: ${props => props.theme.rem(14)};
      border-radius: ${props => props.theme.rem(8)};
      color: ${props => props.theme.typography.invertedColor};
      border: none;
      text-decoration: none;
      border-radius: 20px;

      &:hover {
        background: ${props => props.theme.header.list_your_place.button_hover_color};
      }
    }

    &.back-to-uniplaces {
      display: flex;
      align-items: center;
      align-self: flex-end;
      height: ${props => props.theme.space(10)};
      padding: 0 ${props => props.theme.space(4)};
      background: ${props => props.theme.header.back_to_uniplaces.button_color};
      font-family: IntervalNextReg, sans-serif;
      font-weight: 600;
      font-size: ${props => props.theme.rem(14)};
      border-radius: ${props => props.theme.rem(8)};
      color: ${props => props.theme.typography.invertedColor};
      text-decoration: none;
      border: 1px solid ${props => props.theme.header.back_to_uniplaces.button_color};

      &:hover {
        background: ${props => props.theme.colors.lightest};
        color: ${props => props.theme.header.back_to_uniplaces.button_color};
        border: 1px solid ${props => props.theme.header.back_to_uniplaces.button_color};
      }

      a {
        &:hover {
          text-decoration: none;
          border-bottom: unset;
        }
      }
    }
  }

  ${DropdownList} {
    width: 250px;
    padding: ${props => props.theme.space(5)} ${props => props.theme.space(4)};
    position: absolute;
    top: 50px;
    right: 0;
    background: ${props => props.theme.header.dropdown.background};
    box-shadow: 0px 3px 10px 0px rgba(34, 66, 81, 0.1);
    border: 1px solid ${props => props.theme.header.dropdown.border};
    border-radius: 8px;

    ${DropdownItem} {
      font-size: ${props => props.theme.rem(14)};
      position: relative;
      height: 34px;
      border-radius: 6px;

      &:not(:last-child) {
        margin-bottom: ${props => props.theme.space(3)};
      }

      &:before {
        content: none;
      }

      &:hover {
        background: ${props => props.theme.header.dropdown.hoverLinks};
      }

      a {
        font-family: IntervalNextReg, sans-serif;
        font-size: ${props => props.theme.rem(14)};
        color: ${props => props.theme.dropdown.color};
        padding-bottom: 0;
        padding-left: ${props => props.theme.space(2)};
      }
    }
  }

  ${DropdownTitle} {
    font-family: IntervalNextReg, sans-serif;
    font-size: ${props => props.theme.rem(14)};
    color: ${props => props.theme.header.label.color};
    margin-bottom: 0;

    &:before {
      right: 6px;
      background: ${props => props.theme.header.label.background};
      opacity: 0.5;
    }

    &:after {
      right: 11px;
      background: ${props => props.theme.header.label.background};
      opacity: 0.5;
    }

    &:hover {
      color: ${props => props.theme.header.label.selected};
      text-decoration: none;

      &:before,
      &:after {
        background: ${props => props.theme.header.label.selected};
      }
    }
  }

  :has(${LanguageSelector}) {
    label {
      display: flex;
      align-items: center;
      height: 40px;
      border-radius: 8px;
      padding-inline: 12px;
      color: ${props => props.theme.header.link.authlistLinkColor};

      &:hover {
        background: ${props => props.theme.header.authLinksBackground};
        color: ${props => props.theme.header.link.color};
      }

      #arrow-down,
      #arrow-up {
        path {
          stroke: ${props => props.theme.header.dropdown.icon};
        }
      }
    }
  }
`;
